import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

type Stage = "password" | "passkey";

export function LoginView({
  title,
  onSuccess,
}: {
  title: string;
  onSuccess: () => void;
}) {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("password");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.status === 429) {
        setError("Too many attempts. Try again in a few minutes.");
        return;
      }
      if (!res.ok) {
        setError("Wrong password.");
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { next?: string; enroll?: boolean };
      setPassword("");
      if (body.next === "passkey") {
        // iOS only lets WebAuthn start from a tap, so this is a second button,
        // not an automatic prompt.
        setStage("passkey");
        return;
      }
      onSuccess();
      // Signed in from outside with no passkey yet: land on Settings to add one.
      // Through the router — a raw history.replaceState is invisible to it.
      if (body.enroll) navigate("/settings", { replace: true });
    } catch {
      setError("Could not reach the console.");
    } finally {
      setBusy(false);
    }
  };

  const usePasskey = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { startAuthentication } = await import("@simplewebauthn/browser");
      const optsRes = await fetch("/api/auth/passkey/login-options", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (optsRes.status === 401) {
        setStage("password");
        setError("That took too long. Enter your password again.");
        return;
      }
      if (!optsRes.ok) {
        setError("Could not start Face ID.");
        return;
      }
      const response = await startAuthentication({ optionsJSON: await optsRes.json() });
      const verify = await fetch("/api/auth/passkey/login-verify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(response),
      });
      if (verify.status === 429) {
        setError("Too many attempts. Try again in a few minutes.");
        return;
      }
      if (!verify.ok) {
        setError("Passkey not accepted.");
        return;
      }
      onSuccess();
    } catch (e) {
      const name = e instanceof Error ? e.name : "";
      setError(
        name === "NotAllowedError" || name === "AbortError"
          ? "Cancelled. Tap to try again."
          : "Face ID did not complete. Tap to try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (stage === "passkey") {
    return (
      <div className="login">
        <div className="login__form">
          <h1 className="login__title">{title}</h1>
          <button
            className="btn btn--primary login__submit"
            type="button"
            onClick={() => void usePasskey()}
            disabled={busy}
            autoFocus
          >
            {busy ? "Waiting for Face ID…" : "Continue with Face ID"}
          </button>
          <button
            className="btn btn--ghost login__submit"
            type="button"
            onClick={() => {
              setStage("password");
              setError("");
            }}
            disabled={busy}
          >
            Back
          </button>
          <p className="login__error" role={error ? "alert" : undefined} aria-hidden={error ? undefined : true}>
            {error || " "}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <form className="login__form" onSubmit={submit}>
        <h1 className="login__title">{title}</h1>
        <label className="visually-hidden" htmlFor="login-password">
          Password
        </label>
        <input
          id="login-password"
          className="login__input"
          type="password"
          name="password"
          autoComplete="current-password"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
        <button className="btn btn--primary login__submit" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {error ? (
          <p className="login__error" role="alert">
            {error}
          </p>
        ) : (
          <p className="login__error" aria-hidden="true">
            {" "}
          </p>
        )}
      </form>
    </div>
  );
}
